import {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { KID_STATUS_COLOR } from '../../../../../utilities/colorUtilities';
import {
  KidDocumentStatus,
  KID_STATUS_LABEL,
  KID_STATUS_TO_PILL,
} from '../types/KidEditorV2.types';
import { v4 as uuidv4 } from 'uuid';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { Document, Page } from 'react-pdf';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import RateReviewIcon from '@mui/icons-material/RateReview';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SidebarEditor from '../components/inputs/SidebarEditor';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { pdfjs } from 'react-pdf';

import { toast } from '../../../../toast/Toast';
import LoadingSpinner from '../../../../ui/LoadingSpinner';
import ErrorBoundaryMessage from '../../../../ui/ErrorBoundaryMessage';
import { getOS } from '../../../../../utilities/helpers/common/system';
import Raptor2Loading from '../../../../feedback/Raptor2Loading';
import Pill from '../../../../ui/Pill';
import SaveLayoutForm, {
  SaveLayoutSubmission,
} from './SaveLayoutTemplateForm';
import {
  transformElementsToKidDataModels,
  transformElementsToKidLayoutTemplateModels,
  transformKidDataModelsToElements,
} from '../utils/kidDataTransformers';
import type {
  KidDocumentForm,
  PdfElement,
} from '../types/KidPdfGeneratorV2.types';
import {
  createComparableSaveSignature,
  SaveComparisonContext,
} from '../../kids/utils/saveComparison.utils';
import { useDispatch } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import {
  setAllowUserToNavigateAway,
  setDontAllowUserToNavigateAway,
} from '../../../../../redux/kids/actions';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TokenService from '../../../../../services/token-service';
import {
  useGetKidDataWithFigures,
  useGetKidMarkerAll,
  useGetKidOverviewData,
  useGetKidTemplateIncompleteDocuments,
  useGetNecessaryKidTemplates,
} from '../../../../../api/kids/hooks/queries';
import {
  useCreateKidLayoutTemplate,
  useGenerateKidDocument,
  useSaveKidDocument,
  useUpdateOverviewStatus,
} from '../../../../../api/kids/hooks/mutations';
import {
  CreateKidLayoutTemplatePayload,
  KidDataResponse,
  KidStatus,
  KidTemplateContentChangedModel,
  KidTemplateModel,
  KidTemplateType,
  SaveKidDocumentPayload,
  UpdateOverviewStatusPayload,
} from '../../../../../api/kids/types';
import { AxiosError } from 'axios';
import type { UseMutationResult } from '@tanstack/react-query';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import { useGetClient } from '../../../../../api/client';
import useConfirmation from '../../../../../hooks/useConfirmation';
import {
  getInvalidTemplateIdsForDocument,
  hasTemplateIdInElements,
  hasTemplateIdInKidData,
  isKidDocumentTemplateIncomplete,
} from '../utils/templateIncomplete.utils';

type SaveKidDocumentMutationApi = Pick<
  UseMutationResult<any, AxiosError<any>, SaveKidDocumentPayload>,
  'mutate' | 'isPending'
>;

type KidDataResponseWithTemplate = KidDataResponse & {
  template?: KidTemplateModel;
};

export type UpdateKidDocumentPayload = {
  client_id: string;
  fund_id: string;
  share_class_id: string;
  document_language: string;
  raw_kid_data_models: PdfElement[];
  updated_kid_templates?: Record<string, string>;
};

interface KidEditorV2Props {
  onSave: () => void;
  onSaveLayout: (values: SaveLayoutSubmission) => Promise<void>;
  onMarkEdited: () => void;
  onReset: () => void;
  hasUnsavedChanges: boolean;
  canSave: boolean;
  canSaveLayout: boolean;
  hasTemplateInKidData: boolean;
  date: string;
  isSaving: boolean;
  isSavingLayout: boolean;
  isMarkingEdited: boolean;
  fundId?: string;
  shareClassId?: string;
  documentStatus?: string;
  rejectionReason?: string;
  documentVersion?: string;
  figureCalculationDate?: string;
  overviewLastUpdatingDatetime?: string;
  documentLanguage: string;
  /** Template ids that are invalid/inconsistent for this document (should be removed). */
  invalidTemplateIds: Set<string>;
  isDocumentTemplateIncomplete: boolean;
  kidId: string;
  userId: string;
  kidIdCreateDate: string;
  saveKidDocument: SaveKidDocumentMutationApi;
}

const KidEditorV2: FunctionComponent<KidEditorV2Props> = ({
  onSave,
  onSaveLayout,
  onMarkEdited,
  onReset,
  hasUnsavedChanges,
  canSave,
  canSaveLayout,
  hasTemplateInKidData,
  date,
  isSaving,
  isSavingLayout,
  isMarkingEdited,
  fundId,
  shareClassId,
  documentStatus,
  rejectionReason,
  documentVersion,
  figureCalculationDate,
  overviewLastUpdatingDatetime,
  invalidTemplateIds,
  isDocumentTemplateIncomplete,
}) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleConfirmation } = useConfirmation();
  const scrollPosRef = useRef<number>(0);
  const OS = getOS();
  const { user_client_id: clientId } = TokenService.getUser()!;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [isSaveLayoutModalOpen, setIsSaveLayoutModalOpen] = useState(false);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const { data: clientData } = useGetClient(clientId);
  const clientName = clientData?.client_name_full ?? '';
  const { control, getValues } = useFormContext<KidDocumentForm>();

  const { data: markers } = useGetKidMarkerAll(clientId, fundId, shareClassId, figureCalculationDate, overviewLastUpdatingDatetime);

  const elements = useWatch({
    name: 'elements',
    control,
  });

  const markerValueLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    (markers || []).forEach((marker) => {
      lookup.set(`${marker.marker_type}:${marker.placeholder}`, marker.value);
    });
    return lookup;
  }, [markers]);

  const resolveMarkerTokensForGenerator = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.replace(
        /\[\[([A-Z_]+):([a-z0-9_]+)\]\]/g,
        (match, markerType, placeholder) =>
          markerValueLookup.get(`${markerType}:${placeholder}`) || match,
      );
    }

    if (Array.isArray(value)) {
      return value.map((item) => resolveMarkerTokensForGenerator(item));
    }

    return value;
  };


  const generateKidDocumentMutation = useGenerateKidDocument();

  // Fetch fund and share class data for names
  const {
    fundName,
    fundNameFull,
    shareClassName,
    shareClassIsin,
    areNamesReady,
    isLoading: isLoadingNames,
  } = useFundAndShareClassName({
    clientId,
    fundId: fundId || '',
    shareClassId,
    pageKey: 'kid-editor-v2',
  });

  const displayFundNameFull =
    fundNameFull || (isLoadingNames ? '…' : fundId || 'Fund');
  const displayShareClassName =
    shareClassName || (isLoadingNames ? '…' : shareClassId || 'Share Class');

  useEffect(() => {
    const isRejected = documentStatus === KidDocumentStatus.Rejected;

    dispatch(
      updateControlBarComponents([
        <BackToOverviewBtn
          backToOverview={() => navigate(RaidrV2RoutePaths.KID_OVERVIEW_V2, { state: { fundId, shareClassId } })}
          dontSetPosition
          key="back_to_overview"
        />,
        <div
          key="kid_editor_title"
          className="ml-4 flex min-w-0 flex-1 flex-col gap-2 pr-2"
        >
          <div className="flex items-center justify-between gap-3">
            <h6 className="truncate text-xl font-semibold text-mainblue">
              {displayFundNameFull} - {displayShareClassName}
            </h6>
            {documentStatus && (
              <>
                {isRejected && rejectionReason ? (
                  <Tooltip
                    title={
                      <div>
                        <p className="mb-1 text-sm font-semibold text-red-800">
                          Rejection Reason:
                        </p>
                        <p className="text-sm text-red-700">
                          {rejectionReason || 'Document has been rejected'}
                        </p>
                      </div>
                    }
                    arrow
                    placement="bottom"
                    slotProps={{
                      tooltip: {
                        className:
                          'rounded-lg border border-red-300 bg-red-50 p-4 shadow-lg',
                      },
                      arrow: {
                        sx: {
                          color: KID_STATUS_COLOR[KidDocumentStatus.Rejected],
                          '&::before': {
                            border: `1px solid ${KID_STATUS_COLOR[KidDocumentStatus.Rejected]}`,
                            boxSizing: 'border-box',
                          },
                        },
                      },
                    }}
                  >
                    <span
                      className="flex cursor-help items-center gap-1.5 rounded px-3 py-1 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Rejected] }}
                    >
                      Rejected
                      <InfoOutlinedIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                ) : (
                  <Pill
                    status={(KID_STATUS_TO_PILL[documentStatus as KidDocumentStatus] as any) ?? 'neutral'}
                    className="rounded px-2 text-lg font-semibold"
                  >
                    {KID_STATUS_LABEL[documentStatus as KidDocumentStatus] ?? documentStatus}
                  </Pill>
                )}
              </>
            )}
          </div>
        </div>,
      ]),
    );
    dispatch(toggleControlBarShowing(true));

    return () => {
      // Cleanup function to reset control bar components and hide the control bar
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [
    dispatch,
    navigate,
    displayFundNameFull,
    displayShareClassName,
    documentStatus,
    rejectionReason,
  ]);

  const refreshPdf = async () => {
    try {

      const payload: KidDocumentForm = {
        client: clientName || '',
        fund_name: fundName,
        fund_name_full: fundNameFull,
        share_class: shareClassName,
        date,
        // Note: `updated_kid_templates` is intentionally *not* sent to the PDF generator.
        elements: (elements || []).map((element) => ({
          ...element,
          content: resolveMarkerTokensForGenerator(element.content) as PdfElement['content'],
        })),
      };

      const response = await generateKidDocumentMutation.mutateAsync(payload);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);

      const iframe = document.querySelector('iframe');
      if (iframe?.src) {
        iframe.src = fileUrl;
        iframe.title = 'testing out';
      }

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      // Setting the worker for pdfjs:
      if (OS != 'linux') {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      }

      setPdfUrl(fileUrl);
      setPdfBlob(blob);
    } catch (error) {
      console.error(
        'Error refreshing PDF:',
        (error as AxiosError).response?.data,
      );
      toast({
        title: 'Failed to refresh PDF',
        message: `${(error as Error)?.message || 'Unknown error'}`,
        type: 'error',
      });
    }
  };

  const [scale, setScale] = useState(1);
  const [customZoom, setCustomZoom] = useState('100');
  const MIN_SCALE = 0.25;
  const MAX_SCALE = 4.0;
  const SCALE_STEP = 0.1;

  const handleZoomIn = () => {
    const newScale = Math.min(scale + SCALE_STEP, MAX_SCALE);
    setScale(newScale);
    setCustomZoom(Math.round(newScale * 100).toString());
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - SCALE_STEP, MIN_SCALE);
    setScale(newScale);
    setCustomZoom(Math.round(newScale * 100).toString());
  };

  const handleResetZoom = () => {
    setScale(1);
    setCustomZoom('100');
  };

  const handleCustomZoomChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value.replace(/[^\d]/g, '');
    setCustomZoom(value);
  };

  const handleCustomZoomBlur = () => {
    let numValue = parseInt(customZoom, 10);
    if (isNaN(numValue)) numValue = 100;

    numValue = Math.max(Math.min(numValue, MAX_SCALE * 100), MIN_SCALE * 100);
    const newScale = numValue / 100;

    setScale(newScale);
    setCustomZoom(numValue.toString());
  };

  const handleCustomZoomKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCustomZoomBlur();
    }
  };

  const onDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf.numPages);
    // Once PDF is loaded, restore scroll position:
    if (pdfContainerRef.current) {
      pdfContainerRef.current.scrollTop = scrollPosRef.current;
    }
  };

  // Add near other event handlers
  const handleScroll = () => {
    if (pdfContainerRef.current) {
      scrollPosRef.current = pdfContainerRef.current.scrollTop;
    }
  };

  const handleDownload = () => {
    if (!pdfBlob) {
      toast({
        title: 'No PDF available',
        message: 'Please wait for the PDF to be generated',
        type: 'error',
      });
      return;
    }

    // Create a download link using the stored blob
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fundName} - ${shareClassIsin} - current.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!areNamesReady) return;

    const timeout = setTimeout(() => {
      refreshPdf();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [elements, clientName, areNamesReady, fundName, fundNameFull, shareClassName]);

  const renderLoadingUI = (
    <div className="flex h-full min-h-full w-full items-center justify-center bg-gray-200">
      <LoadingSpinner />
    </div>
  );

  const isBaseVersion =
    documentVersion != null
      ? (() => {
        const parts = documentVersion.split('.');
        if (parts.length < 3) return false;
        const patch = Number(parts[2]);
        return !Number.isNaN(patch) && patch === 0;
      })()
      : false;

  const tooltips = {
    download: !pdfBlob || generateKidDocumentMutation.isPending
      ? 'PDF not yet generated.'
      : 'Download the current PDF.',
    reset: !hasUnsavedChanges
      ? 'No unsaved edits to reset.'
      : 'Discard all unsaved edits and restore the last saved state.',
    save: documentStatus === KidDocumentStatus.Edited
      ? 'Document is marked Ready for Review. Use "Mark as Draft" to make edits and save again.'
      : !hasUnsavedChanges
        ? 'No edits to save yet.'
        : isSaving
          ? 'Saving…'
          : 'Save your current edits.',
    saveLayout: hasTemplateInKidData
      ? 'Save Layout is not available for documents that include template fields.'
      : !canSaveLayout
        ? 'No layout elements to save yet.'
        : isSavingLayout
          ? 'Saving layout…'
          : 'Save the current form as a reusable layout template.',
    markStatus: documentStatus === KidDocumentStatus.Rejected
      ? 'Last edits have been rejected. Please make and save new edits before submitting.'
      : isBaseVersion
        ? 'There are no saved edits to submit.'
        : documentStatus === KidDocumentStatus.Editing &&
            isDocumentTemplateIncomplete
          ? 'This document has missing or invalid templates. Resolve template issues before submitting for review.'
          : documentStatus === KidDocumentStatus.Editing
            ? 'Submit this document for review.'
            : 'Return this document to draft.',
  };

  const markStatusColor = documentStatus === KidDocumentStatus.Editing
    ? KID_STATUS_COLOR[KidDocumentStatus.Edited]
    : KID_STATUS_COLOR[KidDocumentStatus.Editing];

  // Colour for the review/draft toggle button — the destination status's colour
  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden gap-3">
      <SidebarEditor
        readOnlyOverlay={
          documentStatus === KidDocumentStatus.Edited ? (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gray-500/50 px-6 py-8"
              aria-hidden="false"
            >
              <div className="rounded-lg border border-gray-200/90 bg-white/95 px-4 py-3 shadow-sm">
                <p className="text-center text-sm font-medium text-gray-700">
                  Document is marked Ready for Review.
                </p>
                <p className="text-center text-xs text-gray-600 mt-0.5">
                  Use &quot;Mark as Draft&quot; below or in the toolbar to edit again.
                </p>
                <div className="mt-3 flex justify-center">
                  <Button
                    onClick={onMarkEdited}
                    disabled={isMarkingEdited}
                    variant="contained"
                    disableElevation
                    size="small"
                    className="normal-case"
                    style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Editing] }}
                    startIcon={isMarkingEdited ? <CircularProgress size={14} color="inherit" /> : <EditNoteIcon style={{ fontSize: 14 }} />}
                  >
                    {isMarkingEdited ? 'Updating…' : 'Mark as Draft'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null
        }
        markers={markers || []}
        invalidTemplateIds={invalidTemplateIds}
      />
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
        <div className="border-gray-200 flex min-h-12 shrink-0 items-center justify-between rounded-t-lg border-b bg-white p-2">
          {/* Left group - Zoom controls */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Tooltip title="Zoom Out" arrow placement="top">
                <span>
                  <Button
                    className={`h-7 w-7 min-w-[28px] border border-gray-200 p-1 hover:bg-gray-500 disabled:cursor-not-allowed disabled:border-gray-100`}
                    variant="outlined"
                    onClick={handleZoomOut}
                    disabled={scale <= MIN_SCALE}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              <div className="relative flex h-7 w-16 items-center rounded border border-gray-300">
                <input
                  className={`h-full w-full rounded border-none p-1 text-center text-sm outline-none focus:ring-2 focus:ring-blue-500`}
                  value={customZoom}
                  onChange={handleCustomZoomChange}
                  onBlur={handleCustomZoomBlur}
                  onKeyDown={handleCustomZoomKeyPress}
                />
                <span className="absolute right-1 text-xs text-gray-500">
                  %
                </span>
              </div>

              <Tooltip title="Zoom In" arrow placement="top">
                <span>
                  <Button
                    className={`h-7 w-7 min-w-[28px] border border-gray-200 p-1 hover:bg-gray-500 disabled:cursor-not-allowed disabled:border-gray-100`}
                    variant="outlined"
                    onClick={handleZoomIn}
                    disabled={scale >= MAX_SCALE}
                  >
                    <ZoomInIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Reset Zoom" arrow placement="top">
                <span>
                  <Button
                    className={`h-7 w-7 min-w-[28px] border border-gray-200 p-1 hover:bg-gray-500 disabled:cursor-not-allowed disabled:border-gray-100`}
                    variant="outlined"
                    onClick={handleResetZoom}
                    disabled={scale === 1}
                  >
                    <RestartAltIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Right group - Actions */}
          <div className="flex items-center gap-1.5">
            <Tooltip title={tooltips.download} placement="top" arrow>
              <span>
                <Button
                  onClick={handleDownload}
                  disabled={!pdfBlob || generateKidDocumentMutation.isPending}
                  variant="contained"
                  disableElevation
                  size="small"
                  className="normal-case !text-white bg-mainblue hover:bg-mainblue/90 disabled:!bg-mainblue disabled:!opacity-40"
                  startIcon={<DownloadIcon style={{ fontSize: 14 }} />}
                >
                  <span className="text-xs">Download</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={tooltips.reset} placement="top" arrow>
              <span>
                <Button
                  onClick={() =>
                    handleConfirmation(
                      'Discard unsaved edits?',
                      'This will restore the last saved state. Any unsaved changes will be permanently lost.',
                      onReset,
                    )
                  }
                  disabled={!hasUnsavedChanges}
                  variant="contained"
                  disableElevation
                  size="small"
                  className="normal-case !text-white bg-red-500 hover:bg-red-600 disabled:!bg-red-500 disabled:!opacity-40"
                  startIcon={<UndoIcon style={{ fontSize: 14 }} />}
                >
                  <span className="text-xs">Reset</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={tooltips.save} placement="top" arrow>
              <span>
                <Button
                  onClick={onSave}
                  disabled={!canSave || isSaving || !hasUnsavedChanges}
                  variant="contained"
                  disableElevation
                  size="small"
                  className="normal-case !text-white bg-mainblue hover:bg-mainblue/90 disabled:!bg-mainblue disabled:!opacity-40"
                  startIcon={
                    isSaving
                      ? <CircularProgress size={12} color="inherit" />
                      : <SaveIcon style={{ fontSize: 14 }} />
                  }
                >
                  <span className="text-xs">Save</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={tooltips.saveLayout} placement="top" arrow>
              <span>
                <Button
                  onClick={() => setIsSaveLayoutModalOpen(true)}
                  disabled={!canSaveLayout || isSavingLayout}
                  variant="contained"
                  disableElevation
                  size="small"
                  className="normal-case !text-white bg-mainblue hover:bg-mainblue/90 disabled:!bg-mainblue disabled:!opacity-40"
                  startIcon={
                    isSavingLayout
                      ? <CircularProgress size={12} color="inherit" />
                      : <SaveIcon style={{ fontSize: 14 }} />
                  }
                >
                  <span className="text-xs">Save Layout</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={tooltips.markStatus} placement="top" arrow>
              <span>
                <Button
                  onClick={onMarkEdited}
                  disabled={
                    isMarkingEdited ||
                    documentStatus === KidDocumentStatus.Rejected ||
                    isBaseVersion ||
                    (documentStatus === KidDocumentStatus.Editing &&
                      isDocumentTemplateIncomplete)
                  }
                  variant="contained"
                  disableElevation
                  size="small"
                  className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
                  style={{ backgroundColor: markStatusColor }}
                  startIcon={
                    isMarkingEdited
                      ? <CircularProgress size={12} color="inherit" />
                      : documentStatus === KidDocumentStatus.Edited
                        ? <EditNoteIcon style={{ fontSize: 14 }} />
                        : <RateReviewIcon style={{ fontSize: 14 }} />
                  }
                >
                  <span className="text-xs">
                    {documentStatus === KidDocumentStatus.Edited ? 'Mark as Draft' : 'Ready for Review'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
        <div
          className="w-full flex-1 min-h-0 overflow-auto rounded-b-md bg-gray-200 scrollbar-custom"
          ref={pdfContainerRef}
          onScroll={handleScroll}
        >
          {generateKidDocumentMutation.error ? (
            <div className="mx-auto my-2.5 flex h-full w-full items-center justify-center bg-white shadow-md">
              <ErrorBoundaryMessage />
            </div>
          ) : (
            <>
              {OS == 'linux' ? (
                <>
                  <iframe
                    src=""
                    width={generateKidDocumentMutation.isPending ? '0%' : '100%'}
                    height="100%"
                    title={'kid.pdf'}
                  ></iframe>
                  {generateKidDocumentMutation.isPending ? (
                    <Raptor2Loading
                      centerWrap
                      messages={['Generating Document...']}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  {(generateKidDocumentMutation.isPending || !pdfUrl) && renderLoadingUI}
                  {pdfUrl && !generateKidDocumentMutation.error && (
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) =>
                        console.error('PDF load error:', error)
                      }
                      loading={renderLoadingUI}
                      className="flex flex-col items-center bg-gray-200"
                    >
                      {Array.from(new Array(numPages), (el, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          className="my-4 block w-fit bg-white shadow-md"
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          width={750 * scale}
                          renderMode="canvas"
                        />
                      ))}
                    </Document>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <SaveLayoutForm
        isOpen={isSaveLayoutModalOpen}
        setIsOpen={setIsSaveLayoutModalOpen}
        clientId={clientId}
        isSaving={isSavingLayout}
        onSubmit={onSaveLayout}
      />
    </div>
  );
};

const KidsEditorFetchWrapper: FunctionComponent = () => {
  const { user_client_id: clientId, user_id: userId } = TokenService.getUser()!;
  const [resetKey, setResetKey] = useState(0);

  const [searchParams] = useSearchParams();
  const fundId = searchParams.get('fundId') || '';
  const shareClassId = searchParams.get('shareClassId') || '';
  const documentLanguage = searchParams.get('documentLanguage') || 'English';

  const hasRequiredParams = Boolean(fundId && shareClassId);

  const {
    data: kidData,
    isLoading: isLoadingKidData,
    isError: isKidDataError,
    error: kidDataError,
  } = useGetKidDataWithFigures(clientId, fundId, shareClassId, documentLanguage);

  const { data: overviewData, isLoading: isLoadingOverview } = useGetKidOverviewData(
    clientId,
    fundId,
    shareClassId,
    documentLanguage,
  );

  const kidId = overviewData?.[0]?.kid_id;

  const { data: necessaryTemplates } = useGetNecessaryKidTemplates(
    clientId,
    fundId,
    documentLanguage,
    {
      enabled: Boolean(clientId && hasRequiredParams),
    },
  );

  const {
    data: incompleteDocuments,
    isLoading: isLoadingIncompleteDocuments,
  } = useGetKidTemplateIncompleteDocuments(clientId, fundId, shareClassId, {
    enabled: Boolean(clientId && hasRequiredParams),
  });

  const necessaryTemplateModelsForEditor = useMemo(() => {
    if (!necessaryTemplates) return [];

    const seen = new Set<string>();
    return Object.values(necessaryTemplates)
      .flatMap((fundTemplates) => fundTemplates.templates)
      .filter((template) => {
        if (!template.template_id || seen.has(template.template_id)) {
          return false;
        }

        seen.add(template.template_id);
        return true;
      });
  }, [necessaryTemplates]);

  const necessaryTemplateLookup = useMemo(
    () =>
      new Map(
        necessaryTemplateModelsForEditor.map((template) => [
          template.template_id,
          template,
        ]),
      ),
    [necessaryTemplateModelsForEditor],
  );

  const kidDataWithTemplateMetadata = useMemo<KidDataResponseWithTemplate[]>(
    () =>
      (kidData || []).map((model) => ({
        ...model,
        template: necessaryTemplateLookup.get(model.template_id),
      })),
    [kidData, necessaryTemplateLookup],
  );

  const invalidTemplateIds = useMemo(
    () =>
      getInvalidTemplateIdsForDocument(
        incompleteDocuments,
        fundId,
        shareClassId,
        documentLanguage,
      ),
    [incompleteDocuments, fundId, shareClassId, documentLanguage],
  );

  const isDocumentTemplateIncomplete = useMemo(
    () =>
      isKidDocumentTemplateIncomplete(
        incompleteDocuments,
        fundId,
        shareClassId,
        documentLanguage,
      ),
    [incompleteDocuments, fundId, shareClassId, documentLanguage],
  );

  const matchingDocument = overviewData?.find(
    (item) =>
      item.fund_id === fundId &&
      item.share_class_id === shareClassId &&
      item.document_language === documentLanguage,
  );

  const documentStatus = matchingDocument?.status;
  const rejectionReason = matchingDocument?.description;
  const documentVersion = matchingDocument?.version_number;

  const date =
    kidData?.[0]?.last_updating_timestamp?.split('T')[0] ??
    new Date().toISOString().split('T')[0];

  const { data: clientData } = useGetClient(clientId);
  const clientNameFull = clientData?.client_name_full ?? '';

  // Build initial form values; when kid data is empty, use empty elements so the editor still renders.
  const initialFormValues: KidDocumentForm | null = useMemo(() => {
    if (!hasRequiredParams) return null;

    return {
      client: clientNameFull,
      fund_name: '',
      fund_name_full: '',
      share_class: '',
      date,
      elements: kidData?.length
        ? transformKidDataModelsToElements(kidDataWithTemplateMetadata)
        : [],
      updated_kid_templates: [],
    };
  }, [
    hasRequiredParams,
    kidData,
    kidDataWithTemplateMetadata,
    date,
    clientNameFull,
  ]);

  // Gate rendering until we have the initial values (requires fundId/shareClassId and loading complete)
  if (!hasRequiredParams) {
    return <div>Missing fundId/shareClassId</div>;
  }

  if (isKidDataError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50">
        <ErrorBoundaryMessage
          message={
            kidDataError?.message ||
            'Failed to load KID document data. Please try refreshing your page.'
          }
        />
      </div>
    );
  }

  if (isLoadingKidData || isLoadingOverview || isLoadingIncompleteDocuments) {
    return <div>Loading…</div>;
  }

  if (!initialFormValues) {
    return <div>Loading…</div>;
  }

  // IMPORTANT: key forces a full remount if user switches fund/shareClass/lang, or resets edits
  const formKey = `${clientId}-${fundId}-${shareClassId}-${documentLanguage}-${date}-${resetKey}`;

  return (
    <KidsEditorForm
      key={formKey}
      clientId={clientId}
      kidId={kidId ?? ''}
      userId={userId}
      figureCalculationDate={overviewData?.[0]?.kid_figure_calculation_date}
      overviewLastUpdatingDatetime={overviewData?.[0]?.last_updating_datetime}
      fundId={fundId}
      shareClassId={shareClassId}
      documentLanguage={documentLanguage}
      date={date}
      documentStatus={documentStatus}
      rejectionReason={rejectionReason}
      documentVersion={documentVersion}
      initialValues={initialFormValues}
      kidData={kidDataWithTemplateMetadata}
      invalidTemplateIds={invalidTemplateIds}
      isDocumentTemplateIncomplete={isDocumentTemplateIncomplete}
      onReset={() => setResetKey((k) => k + 1)}
    />
  );
};

export default KidsEditorFetchWrapper;


type KidsEditorFormProps = {
  clientId: string;
  userId: string;
  kidId: string;
  fundId: string;
  shareClassId: string;
  documentLanguage: string;
  date: string;
  figureCalculationDate?: string;
  overviewLastUpdatingDatetime?: string;
  documentStatus?: string;
  rejectionReason?: string;
  documentVersion?: string;
  initialValues: KidDocumentForm;
  kidData: KidDataResponse[];
  invalidTemplateIds: Set<string>;
  isDocumentTemplateIncomplete: boolean;
  onReset: () => void;
};

const KidsEditorForm: FunctionComponent<KidsEditorFormProps> = ({
  clientId,
  kidId,
  fundId,
  userId,
  shareClassId,
  documentLanguage,
  date,
  figureCalculationDate,
  overviewLastUpdatingDatetime,
  documentStatus,
  rejectionReason,
  documentVersion,
  initialValues,
  kidData,
  invalidTemplateIds,
  isDocumentTemplateIncomplete,
  onReset,
}) => {

  // TODO: THIS STATEMANAGEMENT CYCLE IS LIKELY TO CAUSE UI SIDEEFFECTS. NEED TO FIX. NOT GOING TO BE EASY.
  // If you expect initialValues object identity to change, freeze it via useMemo.
  // With the `key` approach above, this component remounts anyway, so it's extra-safe.
  // This is not the RHF way of doing things but so its hacky and not ideal or readable. 
  // Need to figure the nested layout form pdf Element structure better to define changes better.
  const defaultValues = useMemo(() => initialValues, [initialValues]);

  const methods = useForm<KidDocumentForm>({
    defaultValues,
    // mode: 'onChange',
    // shouldUnregister: false,
  });
  const { reset } = methods;

  // Refetches after an update can change `initialValues` but RHF does not re-apply
  // `defaultValues` unless we reset. `formKey` above often stays the same (date is only the
  // calendar day), so without this the sidebar keeps stale `elements`.
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const saveKidDocumentMutation = useSaveKidDocument();
  const createKidLayoutTemplateMutation = useCreateKidLayoutTemplate();
  const updateOverviewStatusMutation = useUpdateOverviewStatus();

  const watchedElements = useWatch({
    control: methods.control,
    name: 'elements',
  }) as PdfElement[] | undefined;
  const activeElements = watchedElements || defaultValues.elements;

  const hasTemplateInKidData = useMemo(
    () =>
      hasTemplateIdInKidData(kidData) ||
      hasTemplateIdInElements(activeElements),
    [kidData, activeElements],
  );

  // For blank document creation
  const kidIdCreateDate =
    kidData?.[0]?.kid_id_create_date || new Date().toISOString().split('T')[0];

  const saveComparisonContext = useMemo<SaveComparisonContext>(
    () => ({
      clientId,
      fundId,
      shareClassId,
      kidId,
      kidIdCreateDate,
    }),
    [clientId, fundId, shareClassId, kidId, kidIdCreateDate],
  );

  const initialSaveSignature = useMemo(
    () =>
      createComparableSaveSignature(initialValues.elements, saveComparisonContext),
    [initialValues.elements, saveComparisonContext],
  );
  const currentSaveSignature = useMemo(
    () =>
      createComparableSaveSignature(
        watchedElements || defaultValues.elements,
        saveComparisonContext,
      ),
    [watchedElements, defaultValues.elements, saveComparisonContext],
  );
  const hasUnsavedChanges = initialSaveSignature !== currentSaveSignature;

  const dispatch = useDispatch();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    if (hasUnsavedChanges) {
      dispatch(setDontAllowUserToNavigateAway());
    } else {
      dispatch(setAllowUserToNavigateAway());
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      dispatch(setAllowUserToNavigateAway());
    };
  }, [dispatch, hasUnsavedChanges]);

  const handleSaveDocument = () => {
    const formValues = methods.getValues();
    const elements = formValues.elements || [];

    const updatedKidDataModels = transformElementsToKidDataModels(
      elements,
      clientId,
      fundId,
      shareClassId,
      kidId,
      userId,
      kidIdCreateDate,
    );

    const payload: SaveKidDocumentPayload = {
      client_id: clientId,
      fund_id: fundId,
      share_class_id: shareClassId,
      document_language: documentLanguage,
      updated_kid_data_models: updatedKidDataModels,
      inserted_kid_ids: [],
      removed_kid_ids: [],
    };

    const templates =
      (formValues.updated_kid_templates || []) as KidTemplateContentChangedModel[];
    if (templates.length) {
      payload.updated_kid_templates = templates;
    }

    saveKidDocumentMutation.mutate(payload, {
      onSuccess: () => {
        methods.setValue('updated_kid_templates', [], { shouldDirty: false });
      },
    });
  };

  const handleSaveLayout = async ({
    layoutName,
    layoutLevel,
    layoutValue,
  }: SaveLayoutSubmission) => {
    const elements = methods.getValues('elements') || [];
    if (
      !elements.length ||
      hasTemplateIdInKidData(kidData) ||
      hasTemplateIdInElements(elements)
    ) {
      return;
    }

    const kidLayoutId = uuidv4();
    const kidLayoutModels = transformElementsToKidLayoutTemplateModels(
      elements,
      {
        clientId,
        kidLayoutId,
        kidLayoutLevel: layoutLevel,
        kidLayoutValue: layoutValue,
        kidLayoutName: layoutName,
        documentLanguage,
        lastUpdatedBy: userId,
      },
    );

    const payload: CreateKidLayoutTemplatePayload = {
      client_id: clientId,
      kid_layout_level: layoutLevel,
      kid_layout_value: layoutValue,
      document_language: documentLanguage,
      kid_layout_id: kidLayoutId,
      kid_layout_name: layoutName,
      kid_layout_models: kidLayoutModels,
    };

    await createKidLayoutTemplateMutation.mutateAsync(payload);
  };

  const handleMarkEdited = () => {
    const newStatus: UpdateOverviewStatusPayload['status_change_to'] =
      documentStatus === KidDocumentStatus.Edited
        ? KidStatus.EDITING
        : KidStatus.EDITED;

    if (
      newStatus === KidStatus.EDITED &&
      isDocumentTemplateIncomplete
    ) {
      return;
    }

    updateOverviewStatusMutation.mutate({
      client_id: clientId,
      fund_id: fundId,
      share_class_id: shareClassId,
      status_change_to: newStatus,
      document_language: documentLanguage,
    });
  };

  // Force a full remount (via the key in the parent) so TipTap editors reinitialise from saved data
  const handleReset = () => onReset();

  return (
    <FormProvider {...methods}>
      <KidEditorV2
        onSave={handleSaveDocument}
        onSaveLayout={handleSaveLayout}
        onReset={handleReset}
        figureCalculationDate={figureCalculationDate}
        overviewLastUpdatingDatetime={overviewLastUpdatingDatetime}
        onMarkEdited={handleMarkEdited}
        hasUnsavedChanges={hasUnsavedChanges}
        canSave={
          Boolean(fundId && shareClassId) &&
          !saveKidDocumentMutation.isPending &&
          documentStatus !== KidDocumentStatus.Edited
        }
        canSaveLayout={Boolean(activeElements?.length) && !hasTemplateInKidData}
        hasTemplateInKidData={hasTemplateInKidData}
        isSaving={saveKidDocumentMutation.isPending}
        isSavingLayout={createKidLayoutTemplateMutation.isPending}
        isMarkingEdited={updateOverviewStatusMutation.isPending}
        fundId={fundId}
        date={date}
        shareClassId={shareClassId}
        documentLanguage={documentLanguage}
        documentStatus={documentStatus}
        rejectionReason={rejectionReason}
        documentVersion={documentVersion}
        invalidTemplateIds={invalidTemplateIds}
        isDocumentTemplateIncomplete={isDocumentTemplateIncomplete}
        kidId={kidId}
        userId={userId}
        kidIdCreateDate={kidIdCreateDate}
        saveKidDocument={saveKidDocumentMutation}
      />
    </FormProvider>
  );
};

